import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarCheck,
  ClipboardList,
  FileText,
  Landmark,
  Image,
  Inbox,
  LayoutDashboard,
  ReceiptIndianRupee,
  TrendingUp,
  UploadCloud,
  Settings,
  Share2,
  Users,
  UsersRound,
  UserCog,
  X,
} from "lucide-react";

import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { ACCESS, hasAnyPermission } from "../lib/permissions";
import { PLAN_FEATURES, hasPlanFeature } from "../lib/planFeatures";

const ADVERTISER_SECTIONS = [
  {
    items: [
      { icon: LayoutDashboard, label: "Home", path: "/dashboard", permissions: ACCESS.dashboard },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        icon: Image,
        label: "Hoardings",
        path: "/hoardings",
        permissions: ACCESS.hoardings,
        match: (location) => location.pathname.startsWith("/hoardings"),
      },
      {
        icon: CalendarCheck,
        label: "Campaigns",
        path: "/campaigns",
        permissions: ACCESS.campaigns,
      },
    ],
  },
  {
    label: "Sales",
    items: [
      {
        icon: ClipboardList,
        label: "Proposals",
        path: "/proposals/create",
        permissions: ACCESS.proposals,
        match: (location) => location.pathname.startsWith("/proposals"),
      },
      {
        icon: Share2,
        label: "Partner Network",
        path: "/partner-network",
        permissions: ACCESS.partnerNetwork,
        settingsFlag: "partnerNetworkSidebar",
      },
      { icon: Users, label: "Clients", path: "/clients", permissions: ACCESS.clients },
    ],
  },
  {
    label: "Finance",
    items: [
      {
        icon: ReceiptIndianRupee,
        label: "Work Queue",
        path: "/finance",
        permissions: ACCESS.financeOps,
        planFeature: PLAN_FEATURES.financeQueue,
      },
      {
        icon: Landmark,
        label: "Accounts",
        path: "/accounts",
        permissions: ACCESS.accounts,
        planFeature: PLAN_FEATURES.accounts,
        match: (location) => location.pathname.startsWith("/accounts"),
      },
      { icon: FileText, label: "Reports", path: "/reports", permissions: ACCESS.reports, planFeature: PLAN_FEATURES.reports },
      { icon: TrendingUp, label: "Analysis", path: "/analysis", permissions: ACCESS.analysis, planFeature: PLAN_FEATURES.analysis },
    ],
  },
  {
    label: "Administration",
    items: [
      {
        icon: UsersRound,
        label: "Employees",
        path: "/employees",
        permissions: ACCESS.employees,
        planFeature: PLAN_FEATURES.employees,
      },
      { icon: Settings, label: "Business", path: "/business", permissions: ACCESS.business },
      { icon: UserCog, label: "Account Settings", path: "/profile" },
    ],
  },
];

const SUPER_ADMIN_NAV = [
  { icon: LayoutDashboard, label: "Overview", path: "/super-admin" },
  { icon: Users, label: "Advertisers", path: "/super-admin/advertisers" },
  { icon: UploadCloud, label: "Data Migration", path: "/super-admin/data-migration" },
  { icon: Inbox, label: "Enquiries", path: "/super-admin/enquiries" },
  { icon: Bell, label: "Notifications", path: "/super-admin/notifications" },
];

const SIDEBAR_SCROLL_KEY = "hx_sidebar_scroll_top";
let sidebarScrollTop = Number(sessionStorage.getItem(SIDEBAR_SCROLL_KEY) || 0);

export function Sidebar({ collapsed = false, setCollapsed, mobileOpen = false, setMobileOpen, onToggle }) {
  const nav = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const isAdvertiserAdmin = user?.role === "advertiser_admin";
  const [partnerNetworkSidebarVisible, setPartnerNetworkSidebarVisible] = useState(false);
  const visible = mobileOpen || !collapsed;
  const expandedWidth = 256;
  const collapsedWidth = 70;

  const initials = (user?.name || user?.email || "U")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const toggle = () => {
    if (onToggle) onToggle();
    else if (setCollapsed) setCollapsed(!collapsed);
  };

  const closeMobile = () => setMobileOpen?.(false);

  useEffect(() => {
    if (!user || isSuperAdmin || !hasAnyPermission(user, ACCESS.partnerNetwork)) {
      setPartnerNetworkSidebarVisible(false);
      return;
    }
    let cancelled = false;
      api.get("/partner-network/settings", { skipGlobalLoader: true })
      .then(({ data }) => {
        if (!cancelled) setPartnerNetworkSidebarVisible(!!data?.enabled);
      })
      .catch(() => {
        if (!cancelled) setPartnerNetworkSidebarVisible(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, user]);

  useEffect(() => {
    const handlePartnerNetworkSidebarChange = (event) => {
      setPartnerNetworkSidebarVisible(!!event.detail?.showInSidebar);
    };
    window.addEventListener("partner-network-sidebar-setting-changed", handlePartnerNetworkSidebarChange);
    return () => {
      window.removeEventListener("partner-network-sidebar-setting-changed", handlePartnerNetworkSidebarChange);
    };
  }, []);

  const isVisible = useCallback((item) => {
    if (item.adminOnly && !isAdvertiserAdmin) return false;
    if (item.permissions?.length && !hasAnyPermission(user, item.permissions)) return false;
    if (item.planFeature && !hasPlanFeature(user, item.planFeature)) return false;
    if (item.settingsFlag === "partnerNetworkSidebar" && !partnerNetworkSidebarVisible) return false;
    return true;
  }, [isAdvertiserAdmin, partnerNetworkSidebarVisible, user]);

  const isActive = useCallback((item) => {
    if (item.match) return item.match(location);
    const exactOnly = item.path === "/dashboard" || item.path === "/super-admin";
    return location.pathname === item.path || (!exactOnly && location.pathname.startsWith(`${item.path}/`));
  }, [location]);

  const advertiserSections = useMemo(() => ADVERTISER_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(isVisible),
  })).filter((section) => section.items.length > 0), [isVisible]);

  const goTo = (path) => {
    if (navRef.current) {
      sidebarScrollTop = navRef.current.scrollTop;
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(sidebarScrollTop));
    }
    nav(path);
    closeMobile();
  };

  useLayoutEffect(() => {
    const node = navRef.current;
    if (!node) return undefined;
    const saved = sidebarScrollTop || Number(sessionStorage.getItem(SIDEBAR_SCROLL_KEY) || 0);
    if (saved > 0) {
      node.scrollTop = saved;
      window.requestAnimationFrame(() => {
        node.scrollTop = saved;
      });
      window.setTimeout(() => {
        node.scrollTop = saved;
      }, 0);
    }

    const saveScroll = () => {
      sidebarScrollTop = node.scrollTop;
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(sidebarScrollTop));
    };
    node.addEventListener("scroll", saveScroll, { passive: true });
    return () => {
      node.removeEventListener("scroll", saveScroll);
    };
  }, []);

  const renderDirectItem = ({ icon: Icon, label, path, ...item }) => {
    const active = isActive({ path, ...item });
    return (
      <button
        key={path}
        onClick={() => goTo(path)}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        title={!visible ? label : undefined}
        className={[
          "w-full flex items-center rounded-lg whitespace-nowrap transition-all duration-150 group relative",
          visible ? "gap-3 px-3 py-2" : "px-0 py-2 justify-center",
          active
            ? "bg-[#2563eb] text-white shadow-md shadow-blue-950/20"
            : "text-[#94A3B8] hover:bg-[#1E293B] hover:text-white",
        ].join(" ")}
        type="button"
      >
        {active && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-white" />
        )}
        {Icon && (
          <span
            className={[
              "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-colors duration-150",
              active ? "bg-blue-800/40 text-white shadow-sm" : "bg-[#1E293B] text-[#94A3B8] group-hover:bg-[#1E293B] group-hover:text-[#F8FAFC]",
            ].join(" ")}
          >
            <Icon
              size={15}
              strokeWidth={active ? 2.2 : 1.9}
              className={`transition-transform duration-150 ${active ? "scale-105" : "group-hover:scale-105"}`}
            />
          </span>
        )}
        {visible && (
          <span className={`min-w-0 truncate text-[13px] leading-none ${active ? "font-semibold" : "font-medium"}`}>
            {label}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-40 lg:hidden"
          onClick={closeMobile}
        />
      )}

      <aside
        data-testid="sidebar"
        className={[
          "fixed md:relative top-0 h-screen md:h-full z-50 flex-shrink-0 flex flex-col",
          "transition-all duration-300 ease-in-out overflow-hidden",
          "shadow-2xl md:shadow-none border-r",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          "border-slate-800",
        ].join(" ")}
        style={{
          width: visible ? expandedWidth : collapsedWidth,
          minWidth: visible ? expandedWidth : collapsedWidth,
          background: "#0F1115",
        }}
      >
        {/* Logo / Brand */}
        <div className={`flex items-center flex-shrink-0 border-b border-slate-800 ${visible ? "px-4 py-3.5" : "p-3 justify-center"}`}>
          <button
            onClick={toggle}
            className="flex items-center gap-3 flex-1 min-w-0 text-left focus:outline-none group"
            aria-label="Toggle sidebar"
            type="button"
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm shadow-blue-950/20 ring-1 ring-blue-500/20 transition-transform duration-200 group-hover:scale-105"
              style={{ background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)" }}
            >
              <span className="text-white font-bold text-lg font-display leading-none">H</span>
            </div>
            {visible && (
              <div className="flex flex-col whitespace-nowrap overflow-hidden">
                <span className="font-bold text-[15px] text-[#F8FAFC] tracking-tight leading-tight">Hoardexa</span>
                <span className="text-[9px] text-[#94A3B8] font-bold tracking-[0.22em] uppercase mt-0.5">Outdoor Ads</span>
              </div>
            )}
          </button>
          {mobileOpen && (
            <button
              onClick={closeMobile}
              className="ml-2 flex-shrink-0 p-1.5 rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#1E293B] transition-colors md:hidden"
              aria-label="Close sidebar"
              type="button"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav
          ref={navRef}
          className="flex-1 overflow-y-auto py-3 scrollbar-hide"
          style={{ scrollbarWidth: "none", padding: visible ? "10px 10px" : "10px 8px" }}
        >
          {isSuperAdmin
            ? (
              <div className="space-y-px">
                {SUPER_ADMIN_NAV.map((item) => renderDirectItem(item))}
              </div>
            )
            : advertiserSections.map((section, index) => (
              <div key={section.label || "home"} className={index === 0 ? "mb-1.5" : "mb-1.5"}>
                {visible && section.label && (
                  <div className="flex items-center gap-2 px-2.5 mb-1.5 mt-3">
                    <span className="text-[9px] font-black uppercase tracking-[0.22em] text-[#94A3B8] whitespace-nowrap select-none">
                      {section.label}
                    </span>
                    <div className="flex-1 h-px bg-slate-800" />
                  </div>
                )}
                {!visible && section.label && index > 0 && (
                  <div className="mx-3 my-2 h-px bg-slate-800" />
                )}
                <div className="space-y-px">
                  {section.items.map((item) => renderDirectItem(item))}
                </div>
              </div>
            ))}
        </nav>

        {/* User info footer */}
        <div className="flex-shrink-0 border-t border-slate-800">
          {visible ? (
            <div className="p-3.5">
              <div className="flex items-center gap-2.5 rounded-lg bg-[#1E293B]/45 px-2.5 py-2.5 shadow-sm ring-1 ring-slate-800 transition-colors">
                <div
                  className="w-8 h-8 flex-shrink-0 rounded-md flex items-center justify-center text-white text-xs font-bold shadow-sm"
                  style={{ background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)" }}
                >
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-semibold text-[#F8FAFC] truncate leading-tight">
                    {user?.name || "User"}
                  </p>
                  <p className="text-[10px] text-[#94A3B8] truncate mt-0.5">{user?.email}</p>
                </div>
                <div className="w-2 h-2 rounded-full flex-shrink-0 bg-emerald-400 shadow-sm shadow-emerald-400/40" title="Online" />
              </div>
            </div>
          ) : (
            <div className="p-3 flex justify-center">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-sm"
                style={{ background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)" }}
                title={user?.name || "User"}
              >
                {initials}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
